import { UserRoleValue } from "constants/field-values";
import type { Guides_Bool_Exp } from "graphql/generated/graphql";
import type { LoggedUserType } from "state/reactive-vars/logged-user-var";

export const canRoleRemove = (userRole: UserRoleValue, targetRole: UserRoleValue) => {
  // currently admin_users cannot be removed by any role
  if (targetRole === UserRoleValue.admin_user) {
    return false
  };

  switch (userRole) {
    case UserRoleValue.admin_user:
      return true;
    case UserRoleValue.vendor_manager:
      return (
        targetRole === UserRoleValue.vendor_manager ||
        targetRole === UserRoleValue.vendor_employee
      );
    case UserRoleValue.customer_manager:
      return (
        targetRole === UserRoleValue.customer_manager ||
        targetRole === UserRoleValue.customer_employee
      );
    case UserRoleValue.authority_manager:
      return (
        targetRole === UserRoleValue.authority_manager ||
        targetRole === UserRoleValue.authority_user
      );
    default:
      return false;
  }
}

export const canRoleEdit = (userRole: UserRoleValue, targetRole: UserRoleValue) => {
  switch (userRole) {
    case UserRoleValue.admin_user:
      return true;
    case UserRoleValue.vendor_manager:
      return (
        targetRole === UserRoleValue.vendor_manager ||
        targetRole === UserRoleValue.vendor_employee
      );
    case UserRoleValue.customer_manager:
      return (
        targetRole === UserRoleValue.customer_manager ||
        targetRole === UserRoleValue.customer_employee
      );
    case UserRoleValue.authority_manager:
      return (
        targetRole === UserRoleValue.authority_manager ||
        targetRole === UserRoleValue.authority_user
      );
    default:
      return false;
  }
}

/**
 * Row filter that narrows the guides list down to what the logged user is allowed to browse.
 * Returns null for the roles that browse every guide, so the caller can leave the list unfiltered.
 *
 * Note this only shapes the listing: a guide reached directly by its URL stays reachable, the same
 * way an anonymous visitor can open a link someone shared with them.
 */
export const buildGuidesVisibilityFilter = (user: LoggedUserType): Guides_Bool_Exp | null => {
	const organizationId = user?.organization_id;
	const ownGuides: Guides_Bool_Exp = { created_by_user_id: { _eq: user?.id } };
	const organizationGuides: Guides_Bool_Exp = { organization_id: { _eq: organizationId } };

	switch (user?.role) {
		case UserRoleValue.admin_user:
			return null;

		case UserRoleValue.customer_manager:
		case UserRoleValue.customer_employee: {
			const propertyIds = (user.user_properties ?? []).map(userProperty => userProperty.property_id);

			// Guides handed over with one of their properties. An empty property list makes
			// `_in` match nothing, which is what a customer without properties should see.
			const visible: Guides_Bool_Exp[] = [{ handover: { property_id: { _in: propertyIds } } }];

			if (organizationId) {
				visible.push(organizationGuides);
				// Guides about a device installed at one of their properties, but only from the
				// vendors their organization actually works with.
				visible.push({
					_and: [
						{ product_guides: { product: { installed_products: { property_id: { _in: propertyIds } } } } },
						{ organization: { customers: { customer_org_id: { _eq: organizationId } } } }
					]
				});
			}

			return { _or: visible };
		}

		case UserRoleValue.vendor_manager:
		case UserRoleValue.vendor_employee:
		case UserRoleValue.authority_manager:
		case UserRoleValue.authority_user:
			return organizationId ? organizationGuides : ownGuides;

		default:
			return ownGuides;
	}
};
